import { useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { AnimatePresence, motion } from "motion/react";
import type { CardView } from "../api/types";
import type { Theme } from "../theme/pixel";
import { factionFor } from "../theme/factions";
import { E32 } from "../theme/endesga32";
import { Card } from "./Card";
import { KeywordIconStrip } from "./KeywordIconStrip";
import { PickOrderBadge } from "./PickOrderBadge";
import { cardFxKey, cardKey, useBoardRefs } from "../logic/boardRefs";
import {
  effectiveLayoutId,
  patchCardView,
  useCardOverrides,
} from "../logic/cardOverrides";
import {
  DURATIONS,
  secondsFor,
  trapFlipTransition,
  trapFlipVariants,
} from "../logic/motion";

export interface FieldInteraction {
  inspectedId: number | null;
  attackers: Set<number>;
  incomingAttackers: Set<number>;
  activeAttacker: number | null;
  blocks: Map<number, Set<number>>;
  /** Dimmed and non-clickable. */
  unavailable: Set<number>;
  /** Legal picks for the open `choose_targets` call. */
  targetCandidates?: Set<number>;
  /** Picked targets, mapped to 1-based pick order for the corner badge. */
  targetPicks?: Map<number, number>;
  /** While a target picker is open, the green inspection ring is hidden
   *  so the amber target ring reads cleanly. The card still shows in the
   *  sidebar; just no extra ring. */
  suppressInspectRing?: boolean;
  onCardClick: (card: CardView) => void;
  onHover?: (card: CardView | null) => void;
  onCardMouseDown?: (card: CardView, ev: React.MouseEvent<HTMLDivElement>) => void;
}

interface Props {
  isYou: boolean;
  faction: string;
  units: CardView[];
  theme: Theme;
  interaction: FieldInteraction;
  fieldRef?: React.Ref<HTMLDivElement>;
  cardRefs?: Map<number, HTMLDivElement | null>;
  muted?: boolean;
}

const ATTACK_RED = E32.rust;

const COUNTER_PALETTE: string[] = [
  E32.rust,
  E32.burntOrange,
  E32.brick,
  E32.ruby,
  E32.scarlet,
  E32.pumpkin,
  E32.amber,
  E32.fern,
  E32.forest,
  E32.pine,
  E32.cobalt,
  E32.cyan,
  E32.hotPink,
  E32.mulberry,
  E32.coral,
];

function counterColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  return COUNTER_PALETTE[h % COUNTER_PALETTE.length];
}

const FIELD_PADDING_Y = 20;
const FIELD_ROW_GAP = 12;

export function Field({
  isYou,
  faction,
  units,
  theme,
  interaction,
  fieldRef,
  cardRefs,
  muted,
}: Props) {
  const f = factionFor(faction);
  const boardRefs = useBoardRefs();
  const cardOverrides = useCardOverrides();

  const innerRef = useRef<HTMLDivElement | null>(null);
  const [fieldHeight, setFieldHeight] = useState(0);
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setFieldHeight(el.clientHeight));
    ro.observe(el);
    setFieldHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);
  const setRefs = (el: HTMLDivElement | null) => {
    innerRef.current = el;
    if (typeof fieldRef === "function") fieldRef(el);
    else if (fieldRef && "current" in fieldRef)
      (fieldRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
  };
  const rowHeight = Math.max(
    0,
    (fieldHeight - FIELD_PADDING_Y - FIELD_ROW_GAP) / 2,
  );

  // Whose-turn-it-is is signalled by the phase chip + SFX, not by a
  // root-level filter — filter values get crossfaded across view
  // transitions and read as cards "flashing grey".
  void muted;
  const outerStyle: CSSProperties = {
    position: "relative",
    background: `${f.color}18`,
    border: `1px solid ${theme.panelEdge}`,
    borderRadius: theme.radius.panel,
    padding: "10px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    justifyContent: "center",
    minHeight: 0,
    height: "100%",
    // `overflow-x: auto` for the horizontal scrollbar when the row
    // exceeds the visible width. CSS coerces `overflow-y` to a
    // matching constrained value once X is `auto`, so the field
    // ends up clipping vertically too — that's fine here because
    // the combat lunge no longer relies on Field's overflow for
    // its vertical bleed. The lunge mounts a position:fixed clone
    // appended to <body> (see `useBattleAnimations.playCombatLunge`),
    // which lives outside Field's overflow context entirely and
    // can extend past the field edges into the surrounding margin
    // / phase-divider seam regardless of Field's overflow settings.
    overflowX: "auto",
    overflowY: "hidden",
    transform: isYou ? "rotateX(180deg)" : undefined,
  };

  const creatures = units.filter((c) => c.type === "creature");
  const nonCreatures = units.filter((c) => c.type !== "creature");

  // Creatures sit closest to the center seam on each side.
  const rows = isYou
    ? [
        { key: "creatures", cards: creatures },
        { key: "noncreatures", cards: nonCreatures },
      ]
    : [
        { key: "noncreatures", cards: nonCreatures },
        { key: "creatures", cards: creatures },
      ];

  return (
    <div ref={setRefs} style={outerStyle}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          justifyContent: "center",
          minHeight: 0,
          height: "100%",
          transform: isYou ? "rotateX(180deg)" : undefined,
        }}
      >
        {rows.map((row) => (
          <div
            key={row.key}
            // `width: max-content` + `margin: 0 auto` centres without
            // breaking horizontal scroll when the row overflows.
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              height: rowHeight,
              minHeight: 0,
              width: "max-content",
              margin: "0 auto",
            }}
          >
            {/* popLayout: a tile that's leaving (died, exiled, bounced)
                stays painted while Motion FLIPs it to its destination
                pile ghost, while the surviving cards in the row shift
                to fill the gap immediately. Without this, Motion would
                FLIP the (invisible) pile ghost from the field's rect
                instead of painting the leaving card, and the kill
                would read as "the creature just vanishes." */}
            <AnimatePresence mode="popLayout" initial={false}>
              {row.cards.map((c) => renderCard(c))}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );

  function renderCard(c: CardView) {
    const isAttacking = interaction.attackers.has(c.entity_id);
    const isIncoming = interaction.incomingAttackers.has(c.entity_id);
    const activeAttacker = interaction.activeAttacker === c.entity_id;
    const unavailable = interaction.unavailable.has(c.entity_id);
    const inspected = interaction.inspectedId === c.entity_id;
    const isTargetCandidate = interaction.targetCandidates?.has(c.entity_id) ?? false;
    const pickOrder = interaction.targetPicks?.get(c.entity_id);
    const isTargetPicked = pickOrder !== undefined;
    const showPickOrder =
      pickOrder !== undefined && (interaction.targetPicks?.size ?? 0) > 1;

    const assigned = interaction.blocks.get(c.entity_id);
    const blockCount = assigned ? assigned.size : 0;
    // Find which attacker (if any) this card is blocking, plus the
    // 1-based index among that attacker's blockers. The defender's
    // declared order is what the engine uses for damage assignment
    // (blocker #1 absorbs first, spillover walks to #2, …), so the
    // badge is a meaningful gameplay readout, not just decoration.
    let isBlocker = false;
    let blockerOrder: number | null = null;
    for (const blockerSet of interaction.blocks.values()) {
      const arr = Array.from(blockerSet);
      const idx = arr.indexOf(c.entity_id);
      if (idx >= 0) {
        isBlocker = true;
        blockerOrder = idx + 1;
        break;
      }
    }
    // Hide the badge when only one blocker faces an attacker — the
    // "1" carries no ordering signal alone.
    const showBlockerOrder =
      blockerOrder !== null && blockerOrder > 0 && (() => {
        for (const set of interaction.blocks.values()) {
          if (set.has(c.entity_id)) return set.size > 1;
        }
        return false;
      })();

    // Blockers lift toward the seam the same way attackers/incoming
    // do — visualises "stepping up to defend". The local `interaction`
    // flags drive the lift while the player is PICKING attackers /
    // blockers (preview during a decision); once the engine commits,
    // we fall through to the entity's `is_attacking` /
    // `blocking_against` flags, which the reducer stamps on
    // `attackers_declared` / `block` events and strips again when a
    // `phase` event leaves combat — so the lift (and ring, below)
    // revert the moment combat ends, not when the next pending
    // decision finally lands. Without the entity-level fallback,
    // committed attackers stayed shifted forward + red all the way
    // through the opponent's turn: `pending` only changes when the
    // NEXT decision arrives, which after "declare attackers" can be
    // a whole opponent turn away.
    const entityAttacking = c.is_attacking;
    const entityBlocking = c.blocking_against != null;
    const combatLift =
      isAttacking || isIncoming || isBlocker || entityAttacking || entityBlocking
        ? isYou
          ? -10
          : 10
        : 0;

    // Green (`selected`) = sidebar inspect; combat state renders as a
    // separate ring outside it so the two don't swallow each other.
    // Targeting suppresses the green ring so the amber target ring
    // is unambiguous.
    const sel = inspected && !interaction.suppressInspectRing;
    const combatColor =
      isAttacking || isIncoming || activeAttacker || entityAttacking
        ? ATTACK_RED
        : isBlocker || entityBlocking
          ? E32.amber
          : undefined;
    const extraRing = combatColor ? { color: combatColor } : undefined;

    // Hide the keyword strip on face-down cards so we don't leak which
    // keywords a hidden trap carries.
    const cardKeywords = c.keywords ?? [];
    const sicknessIcon = !c.face_down && c.type === "creature" && !!c.summoning_sickness;
    const showKeywords =
      !c.face_down && c.type === "creature" && (cardKeywords.length > 0 || sicknessIcon);
    const reserveStrip = !c.face_down && c.type === "creature";
    // Keyword icons are 32×32 native. Drawing the regular ones at 32
    // renders them 1:1 (crisp pixels, no fractional downscale) and a good
    // ~60% larger than before. Spawn tokens take 24 (the same 0.75× the
    // token card itself is scaled to), keeping the "lesser spawn" read.
    // The reserved slot adds the strip's 4px top margin so a single row
    // never spills into the card art above it.
    const stripSlotHeight = c.summon_only ? 28 : 36;
    const iconSize = c.summon_only ? 24 : 32;

    // Slot matches the card's rendered size (5/7 aspect). Troops always
    // render at 0.75× the regular slot so they read as spawns; without
    // anchoring the ratio, a tight row height collapses both sizes to
    // the same `Math.min(cardWidth, …)` cap and the differentiation
    // disappears on compact layouts.
    const availableHeight = Math.max(0, rowHeight - stripSlotHeight);
    const regularSlotWidth = Math.min(128, (availableHeight * 5) / 7);
    const slotWidth = c.summon_only
      ? Math.round(regularSlotWidth * 0.75)
      : regularSlotWidth;
    const slotHeight = (slotWidth * 7) / 5;
    return (
      <motion.div
        // The OUTER tile is the motion.div so that the keyword strip,
        // damage marker, and pick-order badges all travel with the
        // card during layout FLIPs between zones. The inner motion.div
        // (the rotateY flip for trap springs) is nested below.
        //
        // `combatLift` (the small lift toward the seam on attackers /
        // blockers) is driven by Motion's `animate.y` with its own
        // transition timing so it composes with the layout transform
        // instead of fighting it.
        key={c.entity_id}
        layoutId={effectiveLayoutId(c.entity_id, cardOverrides)}
        animate={{ y: combatLift }}
        transition={{
          layout: {
            duration: secondsFor(DURATIONS.zoneMove),
            ease: [0.2, 0.9, 0.2, 1],
          },
          y: { duration: 0.18, ease: [0.2, 0.9, 0.2, 1] },
        }}
        ref={(el) => {
          cardRefs?.set(c.entity_id, el);
          boardRefs.register(cardKey(c.entity_id), el);
        }}
        data-card-tile="true"
        style={{
          position: "relative",
          width: slotWidth,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: 0,
        }}
      >
        <div
          // Inner FX wrapper. Stays as a plain div so the combat-lunge
          // overlay can read its rect via boardRefs without grabbing a
          // mid-FLIP transformed bounding box.
          ref={(el) => boardRefs.register(cardFxKey(c.entity_id), el)}
          style={{ width: slotWidth, height: slotHeight }}
        >
          <motion.div
            animate={
              cardOverrides.flipping.has(c.entity_id)
                ? trapFlipVariants.flipping
                : trapFlipVariants.faceDown
            }
            transition={trapFlipTransition()}
            style={{ width: "100%", height: "100%" }}
          >
            <Card
              card={patchCardView(c, cardOverrides)}
              size={c.summon_only ? "mini" : "small"}
              theme={theme}
              faceDown={patchCardView(c, cardOverrides).face_down}
              onHover={interaction.onHover}
              onClick={() => interaction.onCardClick(c)}
              onMouseDown={
                interaction.onCardMouseDown
                  ? (ev) => interaction.onCardMouseDown?.(c, ev)
                  : undefined
              }
              selected={sel}
              extraRing={extraRing}
              dim={unavailable}
              ringColor={
                isTargetPicked
                  ? E32.scarlet
                  : isTargetCandidate
                    ? `${E32.amber}80`
                    : undefined
              }
              ringWidth={isTargetPicked ? 5 : undefined}
              badge={blockCount > 1 ? `×${blockCount}` : undefined}
            />
          </motion.div>
        </div>
        {reserveStrip && (
          <div
            className="doi-keyword-strip"
            style={{
              width: "100%",
              minHeight: stripSlotHeight,
              display: "flex",
              alignItems: "center",
              gap: 4,
              flexWrap: "wrap",
              flexShrink: 0,
              justifyContent: "center",
            }}
          >
            {showKeywords && (
              <KeywordIconStrip
                keywords={cardKeywords}
                theme={theme}
                size={iconSize}
                summoningSickness={sicknessIcon}
              />
            )}
            {(c.named_counters ?? []).map((nc) => (
              <span
                key={nc.name}
                title={nc.name}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: iconSize,
                  height: iconSize,
                  padding: "0 4px",
                  fontFamily: theme.fontDisplay,
                  fontSize: Math.max(9, iconSize - 9),
                  fontWeight: 700,
                  color: counterColor(nc.name),
                  border: `1px solid ${counterColor(nc.name)}`,
                  borderRadius: 2,
                  lineHeight: 1,
                }}
              >
                {nc.count}
              </span>
            ))}
          </div>
        )}
        {c.damage_marked > 0 && (
          <div
            style={{
              position: "absolute",
              top: -6,
              left: -6,
              padding: "1px 6px",
              background: E32.rust,
              color: E32.white,
              fontFamily: theme.fontDisplay,
              fontSize: 9,
              border: `2px solid ${theme.bgDeep}`,
              zIndex: 2,
            }}
          >
            −{c.damage_marked}
          </div>
        )}
        {showPickOrder && pickOrder !== undefined && (
          <PickOrderBadge order={pickOrder} theme={theme} />
        )}
        {showBlockerOrder && blockerOrder !== null && (
          <PickOrderBadge order={blockerOrder} theme={theme} />
        )}
      </motion.div>
    );
  }
}
